import { useGridApiRef, GridPivotModel, GridApiPremium } from '@mui/x-data-grid-premium';
import type { CiqGridValidRowModel } from 'muigrid/src/types/types';
import { MutableRefObject, useState, useMemo, useEffect } from 'react';

import { TableContainer } from './TableContainer.styles';

import type { MetricDatum, ChartClientState } from '../../types';
import { MuiTable, MuiDataGridPivotProps } from '../MuiTable';
import type { VisualizationColumn } from '../MuiTable/types/mui-table';

// type CiqGridValidRowModel = { id: string; [key: string]: any };

export type TableProps = {
  data: MetricDatum[];
  columns: VisualizationColumn[];
  // Props for pivot functionality (matching DashboardLayout)
  pivotModel?: GridPivotModel;
  pivotActive?: boolean;
  pivotPanelOpen?: boolean;
  disablePivoting?: boolean;
  // Callback for client state changes
  onClientConfigChange?: (updates: Partial<ChartClientState>) => void;
  // Callback for pivot configuration persistence
  onPivotConfigSave: (payload: {
    table_chart_grid_pivot_model: GridPivotModel;
    table_chart_grid_pivot_active: boolean;
  }) => void;
  // Optional backward compatibility props
  gridPivotModel?: GridPivotModel;
  gridPivotModelActive?: boolean;
  isLoading?: boolean;
  onPivotModelChange?: (model: GridPivotModel) => void;
  onPivotActiveChange?: (active: boolean) => void;
  onPivotPanelOpenChange?: (open: boolean) => void;
};

const prepareRecords = (data: MetricDatum[]): CiqGridValidRowModel[] =>
  data
    // Filter out empty entries
    .filter((datum) => Object.values(datum).some((value) => Boolean(value)))
    // Map to grid row models
    .map((datum, idx) => ({
      id: Object.entries(datum)
        // Build key=value pairs
        .map(([key, value]) => `${key}=${value}`)
        // Add item position to avoid duplicates - it is stable from the data perspective
        .concat(idx.toString())
        .join('|'),
      ...datum,
    }));

const emptyGridPivotModel: GridPivotModel = {
  rows: [],
  columns: [],
  values: [],
};

export function Table({
  data,
  columns,
  // New props from DashboardLayout
  pivotModel: propPivotModel,
  pivotActive: propPivotActive,
  pivotPanelOpen: propPivotPanelOpen,
  onClientConfigChange,
  // Callback for pivot configuration persistence
  onPivotConfigSave,
  gridPivotModel,
  gridPivotModelActive,
  onPivotPanelOpenChange,
  disablePivoting = false, // Enable pivoting by default for tables
}: TableProps) {
  // Use new props if available, fallback to old props for compatibility
  const initialPivotModel = propPivotModel ?? gridPivotModel ?? emptyGridPivotModel;
  const initialPivotActive = propPivotActive ?? gridPivotModelActive ?? false;
  const initialPivotPanelOpen = propPivotPanelOpen ?? false;

  const [pivotModel, setPivotModel] = useState<GridPivotModel | undefined>(initialPivotModel);
  const [pivotModelActive, setPivotModelActive] = useState<boolean>(initialPivotActive);
  const [isPivotPanelOpen, setIsPivotPanelOpen] = useState<boolean>(initialPivotPanelOpen);

  // Sync prop changes with internal state (critical for DashboardLayout control)
  useEffect(() => {
    if (propPivotPanelOpen !== undefined && propPivotPanelOpen !== null) {
      setIsPivotPanelOpen(propPivotPanelOpen);
    }
  }, [propPivotPanelOpen]);

  useEffect(() => {
    if (propPivotModel) {
      setPivotModel(propPivotModel);
    }
  }, [propPivotModel]);

  useEffect(() => {
    if (propPivotActive !== undefined && propPivotActive !== null) {
      setPivotModelActive(propPivotActive);
    }
  }, [propPivotActive]);

  // Clean up field names and data for MUI Data Grid compatibility
  const records = useMemo(() => prepareRecords(data), [data]);
  const dataGridRef = useGridApiRef() as MutableRefObject<GridApiPremium>;

  const handlePivotPanelOpenChange = (open: boolean) => {
    setIsPivotPanelOpen(open);
    // Notify DashboardLayout of client state change
    onClientConfigChange?.({ tableChartPivotPanelOpen: open });
    // Backward compatibility
    onPivotPanelOpenChange?.(open);
  };

  const handlePivotModelChange = (model: GridPivotModel) => {
    setPivotModel(model);

    const pivotConfigPayload = {
      table_chart_grid_pivot_model: model,
      table_chart_grid_pivot_active: pivotModelActive,
    };

    onPivotConfigSave(pivotConfigPayload);
  };

  const handlePivotActiveChange = (active: boolean) => {
    setPivotModelActive(active);

    const pivotConfigPayload = {
      table_chart_grid_pivot_model: pivotModel || emptyGridPivotModel,
      table_chart_grid_pivot_active: active,
    };
    onPivotConfigSave(pivotConfigPayload);
  };

  // Build pivot props object for MUI DataGrid
  const pivotProps: MuiDataGridPivotProps = {
    disablePivoting,
    pivotModel,
    pivotActive: pivotModelActive,
    pivotPanelOpen: isPivotPanelOpen,
    onPivotModelChange: handlePivotModelChange,
    onPivotActiveChange: handlePivotActiveChange,
    onPivotPanelOpenChange: handlePivotPanelOpenChange,
  };

  return (
    <TableContainer>
      <MuiTable
        columns={columns}
        controlledDataGridRef={dataGridRef}
        isLoading={false}
        pivotProps={pivotProps}
        records={records}
      />
    </TableContainer>
  );
}
